import Iconify from "@/components/ui/Iconify";
import PostAvatar from "@/components/ui/PostAvatar";
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import PostCmtInputHeartCollectControls from "./content-cmt-input-heart-collect-controls";
import { postCommentMediaUpload, createPostComment } from "@/api/comment";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { enqueueSnackbar } from "notistack";
import PostCmtInpUserSuggestionBox from "./content-cmt-inp-user-suggestion-list";
import PostCmtInpEmojiBox from "./content-cmt-inp-emoji-box";
import PostCmtMentionInput from "./content-cmt-mention-input";
import AlbumAlert from "@/components/content-modal-comps/album-alert";
import { useTranslations } from "@/lib/static-intl";
import { EVENT_TYPES, sendAnalyticsEvent } from "@/utils/AnalyticsEvents";
import SmartTranslation from "@/components/ui/SmartTranslation";

const ContentCommentInputSection = ({
  allPosts,
  setAllPosts,
  totalComments,
  allUsersForTagging,
  selectedPostInModal,
  currentCommentInReply,
  scrollToCommentSection,
  setSelectedPostInModal,
  setCurrentCommentInReply,
  isLikeSectionVisible = true,
}) => {
  const apiT = useTranslations("api");
  const explorePostDetailsT = useTranslations("explore.explore_post_details");
  const textAreaRef = useRef(null);
  const [textValue, setTextValue] = useState("");
  const { user, openLoginModal } = useAuthContext();
  const [isEmojiBox, setIsEmojiBox] = useState(false);
  const [commentMedia, setCommentMedia] = useState(null);
  const [collectLoader, setCollectLoader] = useState(false);
  const [userProfilePic, setUserProfilePic] = useState(null);
  const [isInputInFocus, setIsInputInFocus] = useState(false);
  const [currentCollectId, setCurrentCollectId] = useState(null);
  const [showAlbemAlert, setShowAlbemAlert] = useState(false);
  const [commentTaggedUsers, setCommentTaggedUsers] = useState([]);
  const [commentInputLoader, setCommentInputLoader] = useState(false);
  const [isUserSuggestionBox, setIsUserSuggestionBox] = useState(false);

  const calculateFormHeight = (
    isInputInFocus,
    commentMedia,
    currentCommentInReply
  ) => {
    if (!isInputInFocus) {
      return "80px";
    }

    if (commentMedia) {
      return currentCommentInReply ? "300px" : "280px";
    }

    return currentCommentInReply ? "170px" : "125px";
  };

  const makeInputFocus = () => {
    if (!user) {
      openLoginModal();
      return;
    }
    setIsInputInFocus(true);
    textAreaRef.current.focus();
  };

  const resetInputFocus = () => {
    if (!user) {
      openLoginModal();
      return;
    }
    setIsInputInFocus(false);
    setTextValue("");
    setCommentMedia(null);
    setCurrentCommentInReply(null);
    setCommentTaggedUsers([]);
  };

  const handleCommentMediaFileChange = (e) => {
    if (!user) {
      openLoginModal();
      return;
    }
    const file = e.target.files[0];

    if (!file) return;

    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      enqueueSnackbar(apiT("file_too_large"), { variant: "warning" });
      return;
    }

    // Set the selected file with a preview
    setCommentMedia(
      Object.assign(file, { preview: URL.createObjectURL(file) })
    );
  };

  const handleInputChange = (e) => {
    if (!user) {
      openLoginModal();
      return;
    }
    const textareaLineHeight = 20;
    const maxRows = 4;

    textAreaRef.current.rows = 1;

    const { value } = e.target;
    if (value[value.length - 1] === "@") {
      setIsUserSuggestionBox(true);
    } else {
      setIsUserSuggestionBox(false);
    }

    setTextValue(e.target.value);

    const { scrollHeight } = textAreaRef.current;
    const rows = Math.floor(scrollHeight / textareaLineHeight);

    textAreaRef.current.rows = Math.min(rows, maxRows);
  };

  const calculateCommentTaggedUsers = (comment, cmtTaggedUser) => {
    if (cmtTaggedUser?.length > 0) {
      const users = cmtTaggedUser.map((user) => {
        if (comment?.includes(`@${user?.username}`)) {
          return {
            userId: user?.id,
            username: user?.username,
          };
        }
      });

      return users.filter((user) => user !== undefined);
    }

    return [];
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!user) {
      openLoginModal();
      return;
    }

    const calculatedCommentTaggedUsers = calculateCommentTaggedUsers(
      textValue,
      commentTaggedUsers
    );

    if (!user?.userId) return;

    if (textValue?.length === 0 && !commentMedia) return;

    try {
      setCommentInputLoader(true);
      const payload = {
        userId: user?.userId,
        content: textValue?.length > 0 ? textValue : "",
        commentTaggedUsers: calculatedCommentTaggedUsers || [],
      };

      if (currentCommentInReply?.id) {
        payload.parentCommentId = currentCommentInReply?.id;
      }

      const response = await createPostComment(selectedPostInModal?.id, payload);

      if (response?.id && commentMedia) {
        const formData = new FormData();
        formData.append("file", commentMedia);
        formData.append("commentId", response.id);
        formData.append("mediaType", commentMedia.type);

        await postCommentMediaUpload(response?.id, formData);
      }
      setSelectedPostInModal({
        ...selectedPostInModal,
        comments: selectedPostInModal?.comment?.length + 1,
      });
      if (selectedPostInModal?.userId !== user?.userId) {
        sendAnalyticsEvent(EVENT_TYPES.interactions, selectedPostInModal?.userId);
      }
      scrollToCommentSection();
      resetInputFocus();
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
        error?.message ||
        apiT("error message"),
        {
          variant: "error",
        }
      );
    } finally {
      setCommentInputLoader(false);
    }
  };

  const handleAtBtnClick = () => {
    if (!user) {
      openLoginModal();
      return;
    }
    handleInputChange({
      target: {
        value: textValue + "@",
      },
    });
    textAreaRef.current.focus();
  };

  useEffect(() => {
    if (user?.userId && allUsersForTagging?.length > 0) {
      setUserProfilePic(
        allUsersForTagging?.find((u) => u?.id === user?.userId)?.profilePicture
      );
    }
  }, [user, allUsersForTagging]);

  useEffect(() => {
    if (currentCommentInReply) {
      makeInputFocus();
    }
  }, [currentCommentInReply]);

  return (
    <motion.form
      className={`w-full p-4 border-t border-black/[0.06] ${!isLikeSectionVisible ? "block space-y-2 dark:bg-[#202020] bg-[#F7F7F5]" : "flex flex-col gap-2 bg-[#fffefa] dark:bg-dark_bg_grey"} dark:border-white/[0.10] absolute bottom-0`}
      animate={{
        height: calculateFormHeight(
          isInputInFocus,
          commentMedia,
          currentCommentInReply
        ),
      }}
      initial={{ height: "80px" }}
      transition={{
        duration: 0.2,
        ease: "linear",
      }}
      onSubmit={handleSubmitComment}
    >
      {!!currentCommentInReply && (
        <div className="w-full flex flex-col text-black dark:text-dark_primary_label">
          <span className="text-sm tracking-tight text-inherit opacity-40">
            {explorePostDetailsT("reply_to")}{" "}
            {currentCommentInReply?.user?.username}
          </span>
          <span className="text-sm tracking-tight max-w-full truncate text-inherit">
            <SmartTranslation isDynamic text={currentCommentInReply?.content} />
          </span>
        </div>
      )}
      {commentMedia && (
        <div className="w-full h-36 flex items-start justify-between px-1">
          <img
            src={commentMedia?.preview}
            alt="preview"
            className="h-full w-auto max-w-48 object-contain rounded border border-black/[0.06] dark:border-white/[0.10]"
          />
          <button
            type="button"
            className="text-xs text-red"
            onClick={() => setCommentMedia(null)}
          >
            {explorePostDetailsT("clear")}
          </button>
        </div>
      )}
      <div className="w-full flex items-center gap-2 justify-between relative">
        {isUserSuggestionBox && (
          <PostCmtInpUserSuggestionBox
            allUsersForTagging={allUsersForTagging}
            handleOutsideClick={() => setIsUserSuggestionBox((prev) => !prev)}
            handleUserClick={(user) => {
              setCommentTaggedUsers((prev) => [...prev, user]);
              handleInputChange({
                target: {
                  value: textValue + `${user?.username} `,
                },
              });
            }}
          />
        )}
        {isEmojiBox && (
          <PostCmtInpEmojiBox
            handleOutsideClick={() => setIsEmojiBox((prev) => !prev)}
            handleEmojiClick={(emoji) =>
              handleInputChange({
                target: {
                  value: textValue + emoji,
                },
              })
            }
          />
        )}
        {showAlbemAlert && (
          <AlbumAlert
            showAlbemAlert={showAlbemAlert}
            setShowAlbemAlert={setShowAlbemAlert}
            currentPostData={selectedPostInModal}
            currentCollectId={currentCollectId}
          />
        )}
        <div
          className={`w-full bg-gray-100 dark:bg-heading_black rounded-full py-2 px-3 flex items-center gap-2 overflow-hidden truncate cursor-text`}
          onClick={makeInputFocus}
        >
          {!isInputInFocus && (
            <PostAvatar src={userProfilePic} className={"min-w-5"} />
          )}
          <PostCmtMentionInput
            textAreaRef={textAreaRef}
            isInputInFocus={isInputInFocus}
            textValue={textValue}
            handleInputChange={handleInputChange}
            makeInputFocus={makeInputFocus}
            commentTaggedUsers={commentTaggedUsers}
          />

          {isInputInFocus && textValue?.length === 0 && (
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="flex items-center gap-1 select-none"
            >
              <button
                type="button"
                className="text-xl"
                onClick={() => handleInputChange({ target: { value: "😆" } })}
              >
                😆
              </button>
              <button
                type="button"
                className="text-xl"
                onClick={() => handleInputChange({ target: { value: "😀" } })}
              >
                😀
              </button>
              <button
                type="button"
                className="text-xl"
                onClick={() => handleInputChange({ target: { value: "😍" } })}
              >
                😍
              </button>
            </motion.div>
          )}
        </div>
        {!isInputInFocus && isLikeSectionVisible && (
          <PostCmtInputHeartCollectControls
            allPosts={allPosts}
            setAllPosts={setAllPosts}
            totalComments={totalComments}
            makeInputFocus={makeInputFocus}
            setShowAlbemAlert={setShowAlbemAlert}
            collectLoader={collectLoader}
            setCollectLoader={setCollectLoader}
            setCurrentCollectId={setCurrentCollectId}
            selectedPostInModal={selectedPostInModal}
            setSelectedPostInModal={setSelectedPostInModal}
          />
        )}
      </div>
      {isInputInFocus && (
        <motion.div
          transition={{ duration: 0.1, ease: "easeInOut" }}
          className="w-full flex items-center justify-between gap-5"
        >
          <div className="flex items-center justify-center w-fit gap-5">
            <Iconify icon={"ph:at-thin"} onClick={handleAtBtnClick} />
            <Iconify
              icon={"mynaui:smile-circle"}
              onClick={() => setIsEmojiBox((prev) => !prev)}
            />
            <label htmlFor="commentMediaDesktop">
              <input
                type="file"
                hidden
                id="commentMediaDesktop"
                accept="image/*"
                multiple={false}
                onChange={handleCommentMediaFileChange}
              />
              <Iconify
                icon={"fluent:image-32-regular"}
                className={"!text-gray-600 dark:!text-white"}
              />
            </label>
          </div>
          <div className="flex items-center justify-center w-fit gap-3 select-none">
            <button
              className={`bg-rose-600 px-4 py-2 rounded-3xl text-white font-bold transition ${((textValue?.length === 0 && !commentMedia) ||
                commentInputLoader) &&
                "cursor-not-allowed opacity-70"
                }`}
              type="submit"
              disabled={
                (textValue?.length === 0 && !commentMedia) || commentInputLoader
              }
            >
              {commentInputLoader ? (
                <Iconify
                  icon={"eos-icons:loading"}
                  className={"!w-6 !h-6 !text-inherit"}
                />
              ) : (
                explorePostDetailsT("send")
              )}
            </button>
            <button
              className="px-3 py-1.5 rounded-3xl border border-black/[0.08] text-light_primary_label dark:border-white/[0.12] dark:text-dark_primary_label"
              onClick={() => resetInputFocus()}
            >
              {explorePostDetailsT("cancel")}
            </button>
          </div>
        </motion.div>
      )}
    </motion.form>
  );
};

export default ContentCommentInputSection;
